/**
 * Proposal Store
 * TASK-396: Interactive Proposal Builder
 *
 * Single source of truth for investment proposals.
 * Shared between the AI chat (tool calls) and the manual UI (click edits).
 * Both paths write to this store — always in sync.
 *
 * Pattern: Zustand with devtools, same as kycChatStore.ts
 */

import { create } from 'zustand';
import type { RiskDelta, VerifiedConstraint } from '../components/portfolio/VerifiedConstraints';
import { devtools } from 'zustand/middleware';

// ============================================================================
// Types
// ============================================================================

export interface TradeInstrument {
  isin: string;
  name: string;
  type: string;
  currency?: string;
}

export interface TradeTaxImpact {
  capitalGains: number;
  withholdingTax: number;
}

export interface Trade {
  id: string;
  action: 'buy' | 'sell';
  instrument: TradeInstrument;
  quantity: number;
  estimatedPrice: number;
  estimatedValue: number;
  taxImpact?: TradeTaxImpact;
}

export interface ImpactMetrics {
  returnDelta: number;
  riskDelta: number;
  esgDelta: number;
  totalFees: number;
  allocationShift: AllocationShiftItem[];
}

export interface AllocationShiftItem {
  category: string;
  currentPct: number;
  afterPct: number;
  delta: number;
}

export interface Proposal {
  id: string;
  title: string;
  category: string;
  rationale: string;
  trades: Trade[];
  status: 'draft' | 'ready' | 'applied';
  createdBy: 'ai' | 'user';
  lastModifiedBy: 'ai' | 'user';
  createdAt: string;
  updatedAt: string;
}

// TASK-396: flat transaction shape shared by PortfolioComposer, portfolioApiService,
// and the AI propose_portfolio_trades tool. No mapping needed between those three.
export interface ComposerTransaction {
  type: 'buy' | 'sell' | 'add_cash';
  symbol?: string;
  name?: string;
  quantity?: number;
  price?: number;
  currency?: string;
  amount?: number;
}

// TASK-396: an AI-suggested proposal waiting to open in the composer for advisor review.
export interface ComposerPrefill {
  transactions: ComposerTransaction[];
  title: string;
  notes: string;
  // TASK-686: which house rails were checked on these trades, and the risk change per
  // substitution. Optional: a prefill from before verification existed carries neither, and
  // the composer then shows nothing rather than "0 checks run".
  verifiedConstraints?: VerifiedConstraint[];
  riskDeltas?: RiskDelta[];
}

export interface PortfolioSnapshot {
  clientId: string;
  totalValue: number;
  positions: Array<{
    isin: string;
    name: string;
    value: number;
    weight: number;
    assetClass: string;
  }>;
  allocationByClass: Record<string, number>;
}

interface ProposalState {
  proposals: Proposal[];
  activeProposalId: string | null;
  portfolioSnapshot: PortfolioSnapshot | null;
  composerPrefill: ComposerPrefill | null;  // TASK-396: AI proposal pending composer review
  isLoading: boolean;
  error: string | null;

  // Proposal CRUD
  setProposals: (proposals: Proposal[]) => void;  // TASK-396: replace list from backend
  addProposal: (proposal: Omit<Proposal, 'id' | 'createdAt' | 'updatedAt'>) => string;
  updateProposal: (id: string, updates: Partial<Proposal>) => void;
  removeProposal: (id: string) => void;
  duplicateProposal: (id: string) => void;
  setActiveProposal: (id: string | null) => void;

  // Trade CRUD
  addTrade: (proposalId: string, trade: Omit<Trade, 'id'>) => void;
  updateTrade: (proposalId: string, tradeId: string, updates: Partial<Trade>) => void;
  removeTrade: (proposalId: string, tradeId: string) => void;

  // Portfolio snapshot
  setPortfolioSnapshot: (snapshot: PortfolioSnapshot) => void;

  // TASK-396: AI proposal → composer hand-off
  setComposerPrefill: (prefill: ComposerPrefill | null) => void;

  // State
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

// ============================================================================
// Helpers
// ============================================================================

const generateId = (): string =>
  `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const now = (): string => new Date().toISOString();

// ============================================================================
// Store
// ============================================================================

const initialState = {
  proposals: [] as Proposal[],
  activeProposalId: null as string | null,
  portfolioSnapshot: null as PortfolioSnapshot | null,
  composerPrefill: null as ComposerPrefill | null,
  isLoading: false,
  error: null as string | null,
};

export const useProposalStore = create<ProposalState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      setProposals: (proposals) => set({ proposals }),

      addProposal: (proposal) => {
        const id = `proposal_${generateId()}`;
        const timestamp = now();
        set((state) => ({
          proposals: [
            ...state.proposals,
            { ...proposal, id, createdAt: timestamp, updatedAt: timestamp },
          ],
          activeProposalId: id,
        }));
        return id;
      },

      updateProposal: (id, updates) =>
        set((state) => ({
          proposals: state.proposals.map((p) =>
            p.id === id ? { ...p, ...updates, updatedAt: now() } : p,
          ),
        })),

      removeProposal: (id) =>
        set((state) => ({
          proposals: state.proposals.filter((p) => p.id !== id),
          activeProposalId: state.activeProposalId === id ? null : state.activeProposalId,
        })),

      duplicateProposal: (id) => {
        const source = get().proposals.find((p) => p.id === id);
        if (!source) return;
        const newId = `proposal_${generateId()}`;
        const timestamp = now();
        set((state) => ({
          proposals: [
            ...state.proposals,
            {
              ...source,
              id: newId,
              title: `${source.title} (copy)`,
              status: 'draft' as const,
              createdAt: timestamp,
              updatedAt: timestamp,
            },
          ],
          activeProposalId: newId,
        }));
      },

      setActiveProposal: (id) => set({ activeProposalId: id }),

      addTrade: (proposalId, trade) => {
        const tradeId = `trade_${generateId()}`;
        set((state) => ({
          proposals: state.proposals.map((p) =>
            p.id === proposalId
              ? {
                  ...p,
                  trades: [...p.trades, { ...trade, id: tradeId }],
                  updatedAt: now(),
                }
              : p,
          ),
        }));
      },

      updateTrade: (proposalId, tradeId, updates) =>
        set((state) => ({
          proposals: state.proposals.map((p) =>
            p.id === proposalId
              ? {
                  ...p,
                  trades: p.trades.map((t) =>
                    t.id === tradeId ? { ...t, ...updates } : t,
                  ),
                  updatedAt: now(),
                }
              : p,
          ),
        })),

      removeTrade: (proposalId, tradeId) =>
        set((state) => ({
          proposals: state.proposals.map((p) =>
            p.id === proposalId
              ? {
                  ...p,
                  trades: p.trades.filter((t) => t.id !== tradeId),
                  updatedAt: now(),
                }
              : p,
          ),
        })),

      setPortfolioSnapshot: (snapshot) => set({ portfolioSnapshot: snapshot }),

      setComposerPrefill: (prefill) => set({ composerPrefill: prefill }),

      setLoading: (loading) => set({ isLoading: loading }),

      setError: (error) => set({ error }),

      reset: () => set(initialState),
    }),
    { name: 'ProposalStore' },
  ),
);

// ============================================================================
// Selectors
// ============================================================================

export const useProposals = () =>
  useProposalStore((state) => state.proposals);

export const useActiveProposal = () =>
  useProposalStore((state) =>
    state.proposals.find((p) => p.id === state.activeProposalId) ?? null,
  );

export const useProposalById = (id: string) =>
  useProposalStore((state) => state.proposals.find((p) => p.id === id) ?? null);

/**
 * Calculate the combined sell/buy balance for a proposal.
 * Returns { sellTotal, buyTotal, balance, isBalanced }.
 */
export function getProposalBalance(proposal: Proposal): {
  sellTotal: number;
  buyTotal: number;
  balance: number;
  isBalanced: boolean;
} {
  const sellTotal = proposal.trades
    .filter((t) => t.action === 'sell')
    .reduce((sum, t) => sum + t.estimatedValue, 0);
  const buyTotal = proposal.trades
    .filter((t) => t.action === 'buy')
    .reduce((sum, t) => sum + t.estimatedValue, 0);
  const balance = sellTotal - buyTotal;
  return { sellTotal, buyTotal, balance, isBalanced: Math.abs(balance) < 100 };
}
